<template>
  <div>
    <div class="d-flex flex-wrap gap-1 align-items-center">
      <!-- Grupos de botones dinámicos -->
      <div 
        v-for="(group, groupIndex) in buttonGroups" 
        :key="groupIndex"
        class="btn-group" 
        role="group"
      >
        <button 
          v-for="(button, buttonIndex) in group.buttons" 
          :key="buttonIndex"
          @click="handleButtonClick(button)"
          :class="getButtonClass(button)"
          :disabled="button.disabled || loading"
          :title="button.tooltip || button.text"
        >
          <i v-if="button.icon" :class="button.icon + ' me-1'"></i>
          {{ button.text }}
          <span v-if="button.badge !== undefined" class="ms-1">
            ({{ button.badge }})
          </span>
        </button>
      </div>
      
      <!-- Botones individuales -->
      <template v-for="(button, index) in individualButtons" :key="'individual-' + index">
        <button 
          @click="handleButtonClick(button)"
          :class="getButtonClass(button)"
          :disabled="button.disabled || loading"
          :title="button.tooltip || button.text"
        >
          <i v-if="button.icon" :class="button.icon + ' me-1'"></i>
          {{ button.text }}
          <span v-if="button.badge !== undefined" class="ms-1">
            ({{ button.badge }})
          </span>
        </button>
      </template>
      
      <!-- Información adicional en el lado derecho -->
      <div v-if="rightInfo" class="ms-auto text-muted small">
        <span v-html="rightInfo"></span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ToolbarComponent',
  emits: ['button-click', 'toolbar-mounted', 'new', 'refresh', 'edit', 'delete', 'save', 'cancel'],
  props: {
    // Grupos de botones (array de arrays)
    buttonGroups: {
      type: Array,
      default: () => []
    },
    // Botones individuales (no agrupados)
    individualButtons: {
      type: Array,
      default: () => []
    },
    // Información a mostrar en el lado derecho
    rightInfo: {
      type: String,
      default: ''
    },
    // Estado de carga global
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleButtonClick(button) {
      // Ejecutar acción personalizada si existe
      if (button.action && typeof button.action === 'function') {
        button.action();
      }
      
      // Emitir evento específico si está definido
      if (button.event) {
        this.$emit(button.event, {
          id: button.id,
          action: button.actionName || button.id,
          button: button
        });
      }
      
      // Emitir evento genérico para el componente padre (compatibilidad)
      this.$emit('button-click', {
        id: button.id,
        action: button.actionName || button.id,
        button: button
      });
    },
    getButtonClass(button) {
      const baseClasses = ['btn'];
      
      // Tamaño del botón
      if (button.size) {
        baseClasses.push(`btn-${button.size}`);
      } else {
        baseClasses.push('btn-sm');
      }
      
      // Tipo/color del botón
      if (button.variant) {
        baseClasses.push(`btn-${button.variant}`);
      } else {
        baseClasses.push('btn-outline-primary');
      }
      
      // Clases adicionales
      if (button.class) {
        if (Array.isArray(button.class)) {
          baseClasses.push(...button.class);
        } else {
          baseClasses.push(button.class);
        }
      }
      
      return baseClasses.join(' ');
    }
  },
  mounted() {
    // Emitir evento al padre para notificar que el toolbar está montado
    this.$emit('toolbar-mounted');
  }
};
</script>

<style >

.toolbar-card {
  background: transparent;
  /*border: 1px solid #90caf9;*/
  box-shadow: none;  
  /*border-radius: 0.375rem;  */
  transition: all 0.2s ease;
  min-height: auto;
}

.toolbar-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.toolbar-body {
  padding: 0.1rem 0.5rem;
}

/* Tema blanco alternativo */
.toolbar-container.theme-white .toolbar-card {
  background: #ffffff;
  border: 1px solid #dee2e6;
}

.toolbar-container.theme-light .toolbar-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
}

/* Estilos para botones en la barra amarilla */
.toolbar-card .btn {
  font-weight: 500;
  border-width: 1px;
  transition: all 0.2s ease;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.2;
}

.toolbar-card .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toolbar-card .btn:active {
  transform: translateY(0);
}

/* Mejorar contraste en fondo azul suave */
.toolbar-card .btn-outline-primary {
  color: #1976d2;
  border-color: #1976d2;
  background-color: rgba(255, 255, 255, 0.9);
}

.toolbar-card .btn-outline-primary:hover {
  background-color: #1976d2;
  border-color: #1976d2;
  color: white;
}

.toolbar-card .btn-outline-danger {
  color: #d32f2f;
  border-color: #d32f2f;
  background-color: rgba(255, 255, 255, 0.9);
}

.toolbar-card .btn-outline-danger:hover {
  background-color: #d32f2f;
  border-color: #d32f2f;
  color: white;
}

.toolbar-card .btn-outline-info {
  color: #0dcaf0;
  border-color: #0dcaf0;
  background-color: rgba(255, 255, 255, 0.8);
}

.toolbar-card .btn-outline-info:hover {
  background-color: #0dcaf0;
  border-color: #0dcaf0;
  color: white;
}

.toolbar-card .btn-outline-warning {
  color: #fd7e14;
  border-color: #fd7e14;
  background-color: rgba(255, 255, 255, 0.8);
}

.toolbar-card .btn-outline-warning:hover {
  background-color: #fd7e14;
  border-color: #fd7e14;
  color: white;
}

.toolbar-card .btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
  background-color: rgba(255, 255, 255, 0.8);
}

.toolbar-card .btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

/* Información del lado derecho */
.text-muted {
  color: #495057 !important;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .toolbar-body {
    padding: 0.1rem 0.5rem;
  }
  
  .d-flex {
    gap: 0.25rem !important;
  }
  
  .btn-group {
    margin-bottom: 0.1rem;
  }
}
</style>